import * as Sentry from '@sentry/browser';
import Swal from 'sweetalert2';
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";

// https://github.com/twbs/examples/blob/main/webpack/src/js/main.js
//
// Import our custom CSS
import '../scss/project.scss';
import '@xyflow/react/dist/style.css';
import 'handsontable/styles/handsontable.min.css';
import 'handsontable/styles/ht-theme-main.min.css';
import 'handsontable/styles/ht-theme-horizon.min.css';

// Import only the Bootstrap components we need
import {Popover} from 'bootstrap';

import './tour'

if (process.env.SENTRY_DSN) {
    const APP_VERSION = `${process.env.APP_VERSION}`
    Sentry.init({
        dsn: process.env.SENTRY_DSN,
        release: `${process.env.SENTRY_RELEASE_NAME}@${APP_VERSION}`,
        integrations: [Sentry.browserTracingIntegration()],
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: process.env.SENTRY_TRACES_SAMPLE_RATE || 1.0,
        environment: process.env.SENTRY_RELEASE_NAME || 'production',
    });
}

document.querySelectorAll('[data-bs-toggle="popover"]')
    .forEach(popover => {
        new Popover(popover)
    })

document.querySelectorAll('[data-bs-toggle="tooltip"]')
    .forEach(tooltip => {
        new Popover(tooltip, {trigger: "hover focus"})
    })

document.addEventListener("htmx:confirm", (e) => {
    if (!e.detail.question) return
    e.preventDefault()
    const elt = e.detail.elt;
    const vals = JSON.parse(elt.getAttribute("hx-vals"));

    if (vals["object_list"]) {
        const checkedObjects = document.querySelectorAll(`input[name=${vals["object_list"]}]:checked`);
        if (checkedObjects.length === 0) {
            document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
            Swal.fire({
                title: `No ${vals["object_name"]} selected.`,
                text: `Please select at least one ${vals["object_name"]}.`,
                icon: 'error',
            });
            return
        }
    }
    if (!vals.type) {
        e.detail.issueRequest(true);
    } else if (vals.type === "confirm") {
        Swal.fire({
            title: `${e.detail.question}`,
            icon: 'warning',
            showCancelButton: true,
        }).then((result) => {
            if (result.isConfirmed) {
                e.detail.issueRequest(true);
            }
        })
    }
})

document.addEventListener("DOMContentLoaded", async () => {
    flatpickr('input[type="date"], .flatpickr', {
        dateFormat: "Y-m-d"
    });

    const storageKey = (element) => {
        const key = element.dataset.selectKey;
        return key ? `selected_ids_${key}` : undefined;
    }

    const getStoredSelection = (element) => {
        const raw = localStorage.getItem(storageKey(element));
        return new Set(raw ? JSON.parse(raw) : []);
    }

    const setStoredSelection = (table, set) => {
        localStorage.setItem(storageKey(table), JSON.stringify([...set]));
    }

    const updateSelection = (table, id, checked) => {
        const selected = getStoredSelection(table);
        checked ? selected.add(id) : selected.delete(id);
        setStoredSelection(table, selected);
    };

    const clearSelection = (element) => {
        localStorage.removeItem(storageKey(element));
    };

    const updateSelectAllState = (table) => {
        const selectAll = table.querySelector('.select-all');
        if (!selectAll) return;
        const checkboxes = table.querySelectorAll('.form-check-input:not(.select-all)');
        selectAll.checked = checkboxes.length > 0 &&
            [...checkboxes].every((cb) => cb.checked);
    };

    const renderSelectionCounts = () => {
        document.querySelectorAll('#selection-count').forEach((el) => {
            const key = el.dataset.selectKey;
            const n = getStoredSelection(el).size;
            if (n > 0) {
                el.innerHTML = `${n} selected
                    <a href="#" class="btn btn-link btn-sm clear-selection p-0 ms-1" data-select-key="${key}">
                        <i class="bi bi-x-circle"></i> Clear selection
                    </a>`;
            } else {
                el.textContent = '';
            }
        });
    };

    const initTables = () => {
        document.querySelectorAll('table[data-select-key]').forEach((table) => {
            const selected = getStoredSelection(table);
            table.querySelectorAll('.form-check-input:not(.select-all)').forEach((checkbox) => {
                checkbox.checked = selected.has(checkbox.value);
                checkbox.addEventListener('change', () => {
                    updateSelection(table, checkbox.value, checkbox.checked);
                    updateSelectAllState(table);
                    renderSelectionCounts();
                });
            });
            const selectAll = table.querySelector('.select-all');
            if (selectAll) {
                selectAll.addEventListener('change', () => {
                    table.querySelectorAll('.form-check-input:not(.select-all)').forEach((checkbox) => {
                        checkbox.checked = selectAll.checked;
                        updateSelection(table, checkbox.value, checkbox.checked);
                    });
                    renderSelectionCounts();
                });
            }
            updateSelectAllState(table);
        });
        renderSelectionCounts();
    }

    const initSelectionCounters = () => {
        document.querySelectorAll('#selection-count').forEach((el) => {
            el.addEventListener('click', (e) => {
                const btn = e.target.closest('.clear-selection');
                if (!btn) return;
                e.preventDefault();
                clearSelection(btn);
                const key = btn.dataset.selectKey;
                document.querySelectorAll(`table[data-select-key="${key}"]`).forEach((table) => {
                    table.querySelectorAll('.form-check-input:not(.select-all)').forEach((cb) => cb.checked = false);
                    updateSelectAllState(table);
                });
                renderSelectionCounts();
            });
        });
    };

    const initAll = () => {
        initTables();
        initSelectionCounters();
    };

    initAll();
    document.body.addEventListener('htmx:afterSwap', initAll);
});

document.body.addEventListener('htmx:afterSwap', function (e) {
    e.target.querySelectorAll('.flatpickr').forEach(function (el) {
        if (!el._flatpickr) {
            flatpickr(el, {
                dateFormat: "Y-m-d",
            });
        }
    });
    document.querySelectorAll('[data-bs-toggle="popover"]')
        .forEach(popover => {
            new Popover(popover)
        })

});

document.addEventListener('change', function (e) {
    if (e.target.matches('table input[type="checkbox"]')) {
        const tr = e.target.closest('tr');
        if (tr) {
            tr.classList.toggle('table-active', e.target.checked);
        }
    }
});
